import { fmt, Moment } from '@chipinside/frontend'
import { MarkdownView } from '@chipinside/markdown'
import { Translate } from '@chipinside/translate'

import { ApiIntegration } from '#/@types/backend'
import {
  countLiveCalves,
  getLiveCalves,
  isLiveCalf,
  onSuccessActionBirthRegister,
} from '#/pages/Farm/Modules/Herd/Animal/Tabs/ReproductionEvents'
import { FormProps, UseFormsProps } from '#/store/hooks/useForms/types'
import { endpoint } from '#/store/services/endpoints'

export type WithFarm = { farm: string }

const farmForms = {
  animal: {
    attach_collar: ({ params }: FormProps<WithFarm & { animal?: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.attach_collar_form(params),
      save: {
        controller: endpoint.farm.collar.attach(params),
        method: 'put',
      },
      title: Translate({ messageKey: 'attach_collar' }),
      width: 'xs',
    }),
    delete: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalName: string | null }>): UseFormsProps => ({
      controller: endpoint.farm.animal.remove_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      level: 'error',
      save: {
        controller: endpoint.farm.animal.remove({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'discard' }),
      },
      subtitle: params?.animalName,
      title: Translate({ messageKey: 'discard_register' }),
      width: 'sm',
    }),

    discard: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.animal.discard.form(params),
      level: 'error',
      save: {
        controller: (values: any) =>
          endpoint.farm.animal.remove({
            animal: values?.slug,
            farm: params?.farm,
          }),
        method: 'delete',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'discard' }),
      },
      title: Translate({ messageKey: 'discard_register' }),
      width: 'sm',
    }),

    discard_many: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: {
          slug: string
          name: string | null
          earring: string
        }[]
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.animal.discard.many_form(params),
      level: 'error',
      params: { animals: params?.selectedAnimals?.map(a => a.slug) },
      save: {
        controller: () =>
          endpoint.farm.animal.discard.many({
            farm: params?.farm,
          }),
        method: 'delete',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'discard' }),
      },
      subtitle: params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', '),
      title: Translate({ messageKey: 'discard_register' }),
      width: 'sm',
    }),

    move: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.move_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.move({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({ messageKey: 'change_group' }),
      width: 'sm',
    }),

    register: ({ onSuccessAction, params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.animal.form(params),
      save: {
        controller: endpoint.farm.animal.register(params),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: () => onSuccessAction?.(),
            title: Translate({ messageKey: 'register_new_animal' }),
          },
        ],
      },
      title: Translate({
        messageKey: 'register_item',
        params: { item: Translate({ lower: true, messageKey: 'animal' }) },
      }),
      width: 'lg',
    }),

    undo_discard: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.undo_discard_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.undo_discard({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({ messageKey: 'undo_discart' }),
      width: 'sm',
    }),

    update: ({ params }: FormProps<WithFarm & { animalSlug: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.update_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.update({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ lower: true, messageKey: 'animal' }) },
      }),
      width: 'lg',
    }),
  },
  animal_note: {
    delete: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; noteId: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: { item: Translate({ lower: true, messageKey: 'note' }) },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.animal.notes.remove({
          animal: params?.animalSlug,
          farm: params?.farm,
          note: params?.noteId,
        }),
        method: 'delete',
      },
      title: Translate({
        messageKey: 'remove_item',
        params: { item: Translate({ lower: true, messageKey: 'note' }) },
      }),
    }),

    many: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.animal.notes.register_many_animals_form(params),
      save: {
        controller: endpoint.farm.animal.notes.register_many_animals(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: { item: Translate({ lower: true, messageKey: 'note' }) },
      }),
    }),

    register: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.animal.notes.register_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.notes.register({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: { item: Translate({ lower: true, messageKey: 'note' }) },
      }),
      width: 'sm',
    }),

    update: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        noteId: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.animal.notes.update_form({
        animal: params?.animalSlug,
        farm: params?.farm,
        note: params?.noteId,
      }),
      save: {
        controller: endpoint.farm.animal.notes.update({
          animal: params?.animalSlug,
          farm: params?.farm,
          note: params?.noteId,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ lower: true, messageKey: 'note' }) },
      }),
      width: 'sm',
    }),
  },
  animal_photo: {
    attach: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; animalName: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.animal.photo.attach_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.photo.attach({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: [params?.animalEarring, params?.animalName].filter(Boolean).join(' - '),
      title: Translate({
        messageKey: 'add_item',
        params: { item: Translate({ lower: true, messageKey: 'photo' }) },
      }),
      width: 'lg',
    }),
    delete: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; animalName: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: { item: Translate({ lower: true, messageKey: 'photo' }) },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.animal.photo.delete({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: [params?.animalEarring, params?.animalName].filter(Boolean).join(' - '),
      title: Translate({
        messageKey: 'remove_item',
        params: { item: Translate({ lower: true, messageKey: 'photo' }) },
      }),
    }),
    edit: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; animalName: string }
    >): UseFormsProps => ({
      // Sim, mesmo do attach
      controller: endpoint.farm.animal.photo.attach_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        // Sim, mesmo do attach
        controller: endpoint.farm.animal.photo.attach({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: [params?.animalEarring, params?.animalName].filter(Boolean).join(' - '),
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ lower: true, messageKey: 'photo' }) },
      }),
      width: 'lg',
    }),
  },

  animal_unfit: {
    delete: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      alert: {
        level: 'warning',
        text: Translate({ messageKey: 'delete_inapt' }),
      },
      message: Translate({ messageKey: 'delete_inapt_message' }),
      save: {
        controller: endpoint.farm.animal.inapt_delete({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'marking' }),
        },
      }),
    }),

    mark: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.inapt_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      level: 'error',
      save: {
        controller: endpoint.farm.animal.inapt({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'mark' }),
      },
      subtitle: params?.animalEarring,
      title: Translate({ messageKey: 'mark_as_item' }),
      width: 'xs',
    }),

    mark_many: ({
      params,
    }: FormProps<
      WithFarm & { selectedAnimals: Array<{ slug: string; earring: string; name: string | null }> }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(animal => [animal.earring, animal.name].filter(Boolean).join('-'))
        .join(', ')

      return {
        controller: endpoint.farm.animal.inapt_many_form({ farm: params?.farm }),
        params: { animals: params?.selectedAnimals?.map(animal => animal.slug) },
        save: {
          controller: endpoint.farm.animal.inapt_many({ farm: params?.farm }),
          method: 'post',
        },
        submitButton: {
          props: { color: 'error' },
          title: Translate({ messageKey: 'mark' }),
        },
        subtitle,
        title: Translate({
          messageKey: 'mark_as_item',
        }),
        width: 'xs',
      }
    },

    unmark: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.inapt_remove_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      level: 'error',
      save: {
        controller: endpoint.farm.animal.inapt({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'unmark' }),
      },
      subtitle: params?.animalEarring,
      title: Translate({ messageKey: 'unmark_as_unfit' }),
      width: 'xs',
    }),

    unmark_many: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: {
          slug: string
          name: string | null
          earring: string
        }[]
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.animal.inapt_remove_many_form({ farm: params?.farm }),
      level: 'error',
      params: { animals: params?.selectedAnimals?.map(a => a.slug) },
      save: {
        controller: endpoint.farm.animal.inapt_remove_many({
          farm: params?.farm,
        }),
        method: 'PUT',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'unmark' }),
      },
      subtitle: params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', '),
      title: Translate({ messageKey: 'unmark_as_unfit' }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.inapt_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      level: 'error',
      save: {
        controller: endpoint.farm.animal.inapt({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'put',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'mark' }),
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'marking' }),
        },
      }),
      width: 'xs',
    }),
  },

  batch: {
    delete: ({
      params,
    }: FormProps<WithFarm & { batchSlug: string; batchName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.batchName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: { item: Translate({ lower: true, messageKey: 'batch' }) },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.batch.self({
          batch: params?.batchSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.batchName,
      title: Translate({
        messageKey: 'remove_item',
        params: { item: Translate({ lower: true, messageKey: 'batch' }) },
      }),
    }),

    move_many: ({
      onClose,
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: { label: string; id: string }[]
        batchSlug: any
      }
    >): UseFormsProps => {
      const MAX_ANIMALS = 20

      const animals = params?.selectedAnimals ?? []

      const selectedAnimals =
        animals.length <= MAX_ANIMALS
          ? animals
          : [
              ...animals.slice(0, MAX_ANIMALS),
              {
                label: Translate({
                  lower: true,
                  messageKey: 'more_animals',
                  params: { count: animals.length - MAX_ANIMALS },
                }),
              },
            ]

      const subtitle = selectedAnimals?.map(({ label }) => label)?.join(', ')

      return {
        controller: endpoint.farm.animal.batch.move_many_form(params),
        onClose,
        params: { batch: params?.batchSlug },
        save: {
          controller: endpoint.farm.animal.batch.move_many(params),
          manipulateParams(values) {
            return {
              ...values,
              animals: params?.selectedAnimals?.map(a => a.id),
              assignment: Moment(values.assignment).format(fmt.api),
              batch_slug: values.batch,
            }
          },
          method: 'put',
        },
        subtitle,
        title: Translate({ messageKey: 'change_group' }),
        width: 'sm',
      }
    },

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.batch.register_form(params),
      save: {
        controller: endpoint.farm.batch.all(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'batch' }),
        },
      }),
      width: 'md',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { batchSlug: string; batchName: string }>): UseFormsProps => ({
      controller: endpoint.farm.batch.update_form({
        batch: params?.batchSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.batch.self({
          batch: params?.batchSlug,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.batchName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'batch' }),
        },
      }),
      width: 'md',
    }),
  },

  batch_diet: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & { batchSlug: string; dietId: string; dietName: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.dietName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'diet_label' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.batch.diet.remove({
          batch: params?.batchSlug,
          diet: params?.dietId,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.dietName,
      title: Translate({
        messageKey: 'remove_item',
        params: { item: Translate({ lower: true, messageKey: 'diet_label' }) },
      }),
    }),

    register: ({ params }: FormProps<WithFarm & { batchSlug: string }>): UseFormsProps => ({
      controller: endpoint.farm.batch.diet.form({
        batch: params?.batchSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.batch.diet.register({
          batch: params?.batchSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'diet_label' }),
        },
      }),
      width: 'lg',
    }),

    update: ({
      params,
    }: FormProps<
      WithFarm & { batchSlug: string; dietId: string; dietName: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.batch.diet.update_form({
        batch: params?.batchSlug,
        diet: params?.dietId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.batch.diet.update({
          batch: params?.batchSlug,
          diet: params?.dietId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.dietName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'diet_label' }),
        },
      }),
      width: 'lg',
    }),
  },

  birth: {
    register: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { eventSlug?: string; calves?: number }>): UseFormsProps => ({
      controller: endpoint.farm.animal.birth.form(params),
      params: { event_slug: params?.eventSlug },
      save: {
        controller: endpoint.farm.animal.birth.register(params),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => params?.calves,
            onClick: () =>
              onSuccessAction?.(
                params?.calves ? { calves: params?.calves, eventSlug: params?.eventSlug } : {},
              ),
            title: Translate({ messageKey: 'register_new_animal' }),
          },
        ],
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'birth' }),
        },
      }),
      width: 'lg',
    }),

    update: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        eventSlug: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.animal.birth.update_form({
        animal: params?.animalSlug,
        event: params?.eventSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.birth.update({
          animal: params?.animalSlug,
          event: params?.eventSlug,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'birth' }),
        },
      }),
      width: 'lg',
    }),
  },

  body_assessment: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; noteDate: string; weightId: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.noteDate as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'body_assessment' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.animal.body_assessment.delete({
          animal: params?.animalSlug,
          farm: params?.farm,
          weight: params?.weightId,
        }),
        method: 'delete',
      },
      subtitle: params?.noteDate,
      title: Translate({
        messageKey: 'remove_item',
        params: { item: Translate({ lower: true, messageKey: 'body_assessment' }) },
      }),
    }),

    quickaction: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.body_assessment.group_register_form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.body_assessment.group_register({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: { item: Translate({ lower: true, messageKey: 'body_assessment' }) },
      }),
      width: 'lg',
    }),

    register: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.animal.body_assessment.register_form_animal({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.body_assessment.register_animal({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: () => onSuccessAction?.(),
            title: Translate({
              messageKey: 'register_item',
              params: {
                item: Translate({ lower: true, messageKey: 'body_assessment' }),
              },
            }),
          },
        ],
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: { item: Translate({ lower: true, messageKey: 'body_assessment' }) },
      }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; noteDate: string; weightId: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.animal.body_assessment.update_form({
        animal: params?.animalSlug,
        farm: params?.farm,
        weight: params?.weightId,
      }),
      save: {
        controller: endpoint.farm.animal.body_assessment.update({
          animal: params?.animalSlug,
          farm: params?.farm,
          weight: params?.weightId,
        }),
        method: 'put',
      },
      subtitle: params?.noteDate,
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ lower: true, messageKey: 'body_assessment' }) },
      }),
      width: 'xs',
    }),
  },

  bull_semen: {
    delete: ({
      params,
    }: FormProps<WithFarm & { semen: string; semenName: string }>): UseFormsProps => ({
      alert: {
        level: 'error',
        text: Translate({ messageKey: 'bull_or_semen_remove_warning' }),
      },
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.semenName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.semen.remove(params),
        method: 'delete',
      },
      subtitle: params?.semenName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
    }),

    link_to_animal: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.semen.link_to_animal.register_form(params),
    }),
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.semen.register_form(params),
      save: {
        controller: endpoint.farm.semen.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
      width: 'md',
    }),

    replace: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.semen.replace_form(params),
      save: {
        controller: endpoint.farm.semen.replace(params),
        method: 'put',
      },
      title: Translate({ messageKey: 'replace_records' }),
    }),

    update: ({ params }: FormProps<WithFarm & { semen: string }>): UseFormsProps => ({
      controller: endpoint.farm.semen.update_form(params),
      save: {
        controller: endpoint.farm.semen.update(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'bull_or_semen' }),
        },
      }),
      width: 'md',
    }),

    update_link_to_animal: ({
      params,
    }: FormProps<WithFarm & { semen: string; semenName: string }>): UseFormsProps => ({
      controller: endpoint.farm.semen.link_to_animal.update_form(params),
      subtitle: params?.semenName,
    }),
  },

  collar: {
    attach_register: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { collar?: string | number }>): UseFormsProps => ({
      controller: params?.collar
        ? endpoint.farm.collar.attach_with_collar_form(params)
        : endpoint.farm.collar.attach_form(params),
      save: {
        controller: endpoint.farm.collar.attach(params),
        method: 'put',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: () => onSuccessAction?.(),
            title: Translate({
              messageKey: 'register_item',
              params: { item: Translate({ lower: true, messageKey: 'new_attach' }) },
            }),
          },
        ],
      },
      title: Translate({ messageKey: 'attach_collar' }),
      width: 'xs',
    }),

    attach_update: ({
      params,
    }: FormProps<WithFarm & { collar: string | number }>): UseFormsProps => ({
      controller: endpoint.farm.collar.update_attach_form(params),
      save: {
        controller: endpoint.farm.collar.update_attach(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ messageKey: 'collar_link' }) },
      }),
      width: 'xs',
    }),

    bulk_detach: ({ params }: FormProps<WithFarm & { collars: number[] }>): UseFormsProps => ({
      buttons: { submit: { text: Translate({ messageKey: 'detach_item' }) } },
      controller: endpoint.farm.collar.bulk_detach_form(params),
      level: 'error',
      save: {
        controller: endpoint.farm.collar.bulk_detach(params),
        manipulateParams: values => ({ ...values, collars: params?.collars }),
        method: 'put',
      },
      subtitle: params?.collars?.join(', '),
      title: Translate({
        messageKey: 'detach_item',
        params: { item: Translate({ lower: true, messageKey: 'collar_plural' }) },
      }),
      width: 'xs',
    }),
    detach: ({ params }: FormProps<WithFarm & { collar: string | number }>): UseFormsProps => ({
      buttons: { submit: { text: Translate({ messageKey: 'detach_item' }) } },
      controller: endpoint.farm.collar.detach_form(params),
      level: 'error',
      save: {
        controller: endpoint.farm.collar.detach(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'detach_item',
        params: { item: Translate({ lower: true, messageKey: 'collar' }) },
      }),
      width: 'xs',
    }),

    swap: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { animalSlug: string }>): UseFormsProps => {
      return {
        controller: endpoint.farm.collar.change_form(params),
        params: {
          animal_removal_slug: params?.animalSlug,
        },
        save: {
          controller: endpoint.farm.collar.change(params),
          method: 'put',
          onSuccessActions: [
            {
              enabled: () => true,
              onClick: () => onSuccessAction?.(),
              title: Translate({ messageKey: 'register_another_swap' }),
            },
          ],
        },
        title: Translate({ messageKey: 'change_collar_between_animals' }),
        width: 'xs',
      }
    },

    swap_cowbaby: ({ params }: FormProps<WithFarm & { animalSlug: string }>): UseFormsProps => {
      return {
        controller: endpoint.farm.collar.cowbaby_change_form({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        params: { animal_removal_slug: params?.animalSlug },
        save: {
          controller: endpoint.farm.collar.cowbaby_change({
            animal: params?.animalSlug,
            farm: params?.farm,
          }),
          method: 'put',
        },
        title: Translate({ messageKey: 'change_collar_between_animals' }),
        width: 'xs',
      }
    },
  },

  customer_center_interactions: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & { interactionId: string; interectionName: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.interectionName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'interaction' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.cs.customer_center.interactions.delete({
          customer_interaction: params?.interactionId,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.interectionName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'interaction' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.cs.customer_center.interactions.register_form(params),
      save: {
        controller: endpoint.cs.customer_center.interactions.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'interaction' }),
        },
      }),
      width: 'md',
    }),

    update: ({
      params,
    }: FormProps<
      WithFarm & { interactionId: string; interectionName: string }
    >): UseFormsProps => ({
      controller: endpoint.cs.customer_center.interactions.update_form({
        customer_interaction: params?.interactionId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.cs.customer_center.interactions.update({
          customer_interaction: params?.interactionId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.interectionName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'interaction' }),
        },
      }),
      width: 'md',
    }),
  },

  customer_center_profile: {
    update: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.cs.customer_center.farm_profile.profile_form(params),
      save: {
        controller: endpoint.cs.customer_center.farm_profile.self(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'farm_profile',
          }),
        },
      }),
      width: 'md',
    }),
  },

  donor: {
    delete: ({
      params,
    }: FormProps<WithFarm & { donorId: string; donorName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.donorName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'donor' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.embryo_donor.delete({
          donor: params?.donorId,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.donorName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'donor' }),
        },
      }),
    }),

    recovery: ({
      params,
    }: FormProps<WithFarm & { donorId: string; donorName: string }>): UseFormsProps => ({
      level: 'warning',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: Translate({ lower: true, messageKey: 'donor' }),
          item: Translate({ lower: true, messageKey: 'restore_this' }),
        },
      }),
      save: {
        controller: endpoint.farm.embryo_donor.restore({
          donor: params?.donorId,
          farm: params?.farm,
        }),
        method: 'patch',
      },
      submitButton: {
        title: Translate({ messageKey: 'restore' }),
      },
      subtitle: params?.donorName,
      title: Translate({
        messageKey: 'restore_item',
        params: {
          item: Translate({ lower: true, messageKey: 'donor' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.embryo_donor.register_form(params),
      save: {
        controller: endpoint.farm.embryo_donor.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'donor' }),
        },
      }),
    }),

    replace: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.embryo_donor.replace_form(params),
      save: {
        controller: endpoint.farm.embryo_donor.replace(params),
        method: 'put',
      },
      title: Translate({ messageKey: 'replace_records' }),
    }),

    update: ({ params }: FormProps<WithFarm & { donorId: string }>): UseFormsProps => ({
      controller: endpoint.farm.embryo_donor.update_form({
        donor: params?.donorId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.embryo_donor.update({
          donor: params?.donorId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'donor' }),
        },
      }),
    }),
  },

  export: {
    general_spreadsheet: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.export.farm.general.form(params),
      save: {
        controller: endpoint.export.farm.general.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: ['exportation', 'general_spreadsheet'],
        separator: [' / '] as any,
      }),
      width: 'md',
    }),

    holstein_spreadsheet: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.export.farm.holstein.form(params),
      save: {
        controller: endpoint.export.farm.holstein.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: ['exportation', 'brazilian_holstein_cattle_association'],
        separator: [' / '] as any,
      }),
      width: 'xs',
    }),
  },

  farm_account: {
    delete: ({ params }: FormProps<WithFarm & { farmName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.farmName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: { item: Translate({ lower: true, messageKey: 'farm' }) },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.remove(params),
        method: 'delete',
      },
      subtitle: params?.farmName,
      title: Translate({
        messageKey: 'remove_item',
        params: { item: Translate({ lower: true, messageKey: 'farm' }) },
      }),
    }),

    register: ({ onSuccessAction, params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.register_form(params),
      save: {
        controller: endpoint.farm.register(params),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: ({ response }) => {
              onSuccessAction?.({ farmSlug: response?.data?.farm?.slug })
            },
            title: Translate({
              messageKey: 'create_item',
              params: {
                item: Translate({ lower: true, messageKey: 'contract' }),
              },
            }),
          },
        ],
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'farm' }),
        },
      }),
      width: 'md',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { farmName: string }>): UseFormsProps => ({
      controller: endpoint.farm.update_form(params),
      save: {
        controller: endpoint.farm.update(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'farm',
          }),
        },
      }),
      width: 'md',
    }),
  },

  farm_ccom: {
    batch_attach: ({
      params,
    }: FormProps<WithFarm & { serial: string; code: string }>): UseFormsProps => ({
      controller: endpoint.farm.c_com.batch_attach_form({
        ccom: params?.serial,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.c_com.batch_attach({
          ccom: params?.serial,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: Translate({
        messageKey: ['c_com', 'topology'],
        params: [{ item: params?.serial as string }, { item: params?.code as string }],
        separator: ', ',
      }),
      title: Translate({
        messageKey: 'attach_item',
        params: {
          item: Translate({ lower: true, messageKey: 'batch_plural' }),
        },
      }),
    }),
  },

  farm_config: {
    handling: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.config.handling.form(params),
      save: {
        controller: endpoint.farm.config.handling.save(params),
        method: 'put',
      },
    }),
    imports: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.config.imports.form(params),
      save: {
        controller: endpoint.farm.config.imports.save(params),
        method: 'put',
      },
    }),
    monitoring: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.config.monitoring.form(params),
      save: {
        controller: endpoint.farm.config.monitoring.save(params),
        method: 'put',
      },
    }),
    okr: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.config.okr.form(params),
      save: {
        controller: endpoint.farm.config.okr.save(params),
        method: 'put',
      },
    }),
    vic: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.vic.form(params),
      save: {
        controller: endpoint.farm.vic.save(params),
        method: 'put',
      },
    }),
  },

  farm_cowcomfort: {
    attach: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.cow_comfort.attach_form(params),
      save: {
        controller: endpoint.farm.cow_comfort.attach(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'attach_item',
        params: { item: Translate({ messageKey: 'cow_comfort' }) },
      }),
      width: 'xs',
    }),
    change: ({
      params,
    }: FormProps<WithFarm & { point: string; cowcomfortCode: string }>): UseFormsProps => ({
      controller: endpoint.farm.cow_comfort.change_form(params),
      params: {
        c_env: params?.cowcomfortCode,
        device_point_id: params?.point,
      },
      save: {
        controller: endpoint.farm.cow_comfort.change(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'change_cow_comfort_between_points',
      }),
      width: 'xs',
    }),
    detach: ({ params }: FormProps<WithFarm & { point: string }>): UseFormsProps => ({
      controller: endpoint.farm.cow_comfort.detach_form(params),
      save: {
        controller: endpoint.farm.cow_comfort.detach(params),
        method: 'delete',
      },
      title: Translate({
        messageKey: 'detach_item',
        params: { item: Translate({ messageKey: 'cow_comfort' }) },
      }),
      width: 'xs',
    }),
  },

  farm_integration: {
    connect: ({
      params
    }: FormProps<
      WithFarm & { integration: ApiIntegration; integrationTitle: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.integrations.connect_form(params),
      subtitle: params?.integrationTitle,
      title: Translate({ messageKey: 'connect' }),
      width: 'md',
    }),
    disconnect: ({
      params,
    }: FormProps<
      WithFarm & { integration: ApiIntegration; integrationTitle: string }
    >): UseFormsProps => ({
      alert: {
        level: 'warning',
        text: Translate({ messageKey: 'disconnect_integration_alert' }),
      },
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.integrationTitle as string,
          item: Translate({ lower: true, messageKey: 'disconnect' }),
        },
      }),
      save: {
        controller: endpoint.farm.integrations.disconnect(params),
        method: 'delete',
      },
      submitButton: { title: Translate({ messageKey: 'disconnect' }) },
      subtitle: params?.integrationTitle,
      title: Translate({ messageKey: 'disconnect' }),
    }),
    settings: ({
      params,
    }: FormProps<
      WithFarm & { integration: ApiIntegration; integrationTitle: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.integrations.connect_form(params),
      subtitle: params?.integrationTitle,
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ lower: true, messageKey: 'settings' }) },
      }),
      width: 'md',
    }),
    sync: ({
      params,
    }: FormProps<
      WithFarm & { integration: ApiIntegration; integrationTitle: string }
    >): UseFormsProps => ({
      level: 'success',
      message: Translate({
        messageKey: 'sync_now_message',
        params: { item: params?.integrationTitle as string },
      }),
      save: {
        controller: endpoint.farm.integrations.sync(params),
        method: 'post',
      },
      submitButton: { title: Translate({ messageKey: 'sync_now' }) },
      subtitle: params?.integrationTitle,
      title: Translate({ messageKey: 'sync_now' }),
    }),
  },

  farm_network: {
    delete: ({
      params,
    }: FormProps<WithFarm & { networkId: string; networkSSID: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.networkSSID as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: { item: Translate({ lower: true, messageKey: 'network' }) },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.management.network.self({
          farm: params?.farm,
          network: params?.networkId,
        }),
        method: 'delete',
      },
      subtitle: params?.networkSSID,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'network' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.management.network.register_form(params),
      save: {
        controller: endpoint.farm.management.network.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'add_item',
        params: { item: Translate({ lower: true, messageKey: 'network' }) },
      }),
      width: 'sm',
    }),
    set_main: ({
      params,
    }: FormProps<WithFarm & { networkId: string; networkSSID: string }>): UseFormsProps => ({
      level: 'info',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({ lower: true, messageKey: 'set_as_main' }),
        },
      }),
      save: {
        controller: endpoint.farm.management.network.set_main({
          farm: params?.farm,
          network: params?.networkId,
        }),
        method: 'post',
      },
      subtitle: params?.networkSSID,
      title: Translate({ messageKey: 'set_as_main' }),
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { networkId: string; networkSSID: string }>): UseFormsProps => ({
      controller: endpoint.farm.management.network.update_form({
        farm: params?.farm,
        network: params?.networkId,
      }),
      save: {
        controller: endpoint.farm.management.network.self({
          farm: params?.farm,
          network: params?.networkId,
        }),
        method: 'put',
      },
      subtitle: params?.networkSSID,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'network' }),
        },
      }),
      width: 'sm',
    }),
  },

  farm_role: {
    delete: ({
      params,
    }: FormProps<WithFarm & { roleSlug: string; roleName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.roleName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'role' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.role.self({
          farm: params?.farm,
          role: params?.roleSlug,
        }),
        method: 'delete',
      },
      subtitle: params?.roleName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
    }),

    duplicate: ({
      params,
    }: FormProps<WithFarm & { roleSlug: string; roleName: string }>): UseFormsProps => ({
      controller: endpoint.farm.role.register_form({
        farm: params?.farm,
        role: params?.roleSlug,
      }),
      save: {
        controller: endpoint.farm.role.duplicate({
          farm: params?.farm,
          role: params?.roleSlug,
        }),
        manipulateParams(values) {
          return {
            ...values,
            ...(params?.roleSlug ? { role_slug: params.roleSlug } : {}),
          }
        },
        method: 'post',
      },
      subtitle: params?.roleName,
      title: Translate({ messageKey: 'duplicate_role' }),
      width: 'xs',
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.role.register_form(params),
      save: {
        controller: endpoint.farm.role.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'role' }),
        },
      }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { roleSlug: string; roleName: string }>): UseFormsProps => ({
      controller: endpoint.farm.role.update_form({
        farm: params?.farm,
        role: params?.roleSlug,
      }),
      save: {
        controller: endpoint.farm.role.self({
          farm: params?.farm,
          role: params?.roleSlug,
        }),
        method: 'put',
      },
      subtitle: params?.roleName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'role',
          }),
        },
      }),
      width: 'xs',
    }),
  },

  farm_tokens: {
    delete: ({
      params,
    }: FormProps<WithFarm & { tokenId: string; tokenName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.tokenName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'token' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.integrations.services.delete({
          farm: params?.farm,
          token: params?.tokenId,
        }),
        method: 'delete',
      },
      subtitle: params?.tokenName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'token' }),
        },
      }),
    }),
  },

  farm_unit: {
    delete: ({
      params,
    }: FormProps<WithFarm & { unitSlug: string; unitName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.unitName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'unit' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.unit.self({
          farm: params?.farm,
          unit: params?.unitSlug,
        }),
        method: 'delete',
      },
      subtitle: params?.unitName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'unit' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.unit.register_form(params),
      save: {
        controller: endpoint.farm.unit.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'unit' }),
        },
      }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { unitSlug: string; unitName: string }>): UseFormsProps => ({
      controller: endpoint.farm.unit.update_form({
        farm: params?.farm,
        unit: params?.unitSlug,
      }),
      save: {
        controller: endpoint.farm.unit.self({
          farm: params?.farm,
          unit: params?.unitSlug,
        }),
        method: 'put',
      },
      subtitle: params?.unitName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'unit' }),
        },
      }),
      width: 'xs',
    }),
  },

  farm_user: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & { userSlug: string; userName: string; userEmail: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.userName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'user' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.user.self({
          farm: params?.farm,
          user: params?.userSlug,
        }),
        method: 'delete',
      },
      subtitle: params?.userEmail,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'user' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.user.register_form(params),
      save: {
        controller: endpoint.farm.user.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'user' }),
        },
      }),
      width: 'md',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { userSlug: string; userName: string }>): UseFormsProps => ({
      controller: endpoint.farm.user.update_form({
        farm: params?.farm,
        user: params?.userSlug,
      }),
      save: {
        controller: endpoint.farm.user.update({
          farm: params?.farm,
          user: params?.userSlug,
        }),
        method: 'put',
      },
      subtitle: params?.userName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'user' }),
        },
      }),
      width: 'md',
    }),
  },

  handling_events: {
    delete: ({
      params,
    }: FormProps<WithFarm & { handlingSlug: string; handlingDate: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'handling' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.handling.remove({
          farm: params?.farm,
          handling: params?.handlingSlug,
        }),
        method: 'delete',
      },
      subtitle: params?.handlingDate,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'handling' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.handling.register_form(params),
      save: {
        controller: endpoint.farm.handling.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'handling' }),
        },
      }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { handlingSlug: string; handlingDate: string }>): UseFormsProps => ({
      controller: endpoint.farm.handling.update_form({
        farm: params?.farm,
        handling: params?.handlingSlug,
      }),
      save: {
        controller: endpoint.farm.handling.update({
          farm: params?.farm,
          handling: params?.handlingSlug,
        }),
        method: 'put',
      },
      subtitle: params?.handlingDate,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'handling' }),
        },
      }),
      width: 'xs',
    }),
  },

  health_events: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        animalName: string | null
        event: string
      }
    >): UseFormsProps => {
      const animal = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        message: Translate({
          messageKey: 'do_you_really_want_to_item',
          params: {
            id: animal,
            item: Translate({
              lower: true,
              messageKey: 'remove_item',
              params: {
                item: Translate({ lower: true, messageKey: 'health_event' }),
              },
            }),
          },
        }),
        save: {
          controller: endpoint.farm.animal.health.event_remove({
            animal: params?.animalSlug,
            event: params?.event,
            farm: params?.farm,
          }),
          method: 'delete',
        },
        subtitle: animal,
        title: Translate({
          messageKey: 'remove_item',
          params: {
            item: Translate({ lower: true, messageKey: 'health_event' }),
          },
        }),
      }
    },

    ignore: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        animalName: string | null
      }
    >): UseFormsProps => {
      const animal = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        alert: {
          level: 'warning',
          text: Translate({ messageKey: 'ignore_temporarily_alert_message' }),
        },
        level: 'warning',
        message: Translate({ messageKey: 'ignore_temporarily_confirm_message' }),
        save: {
          controller: endpoint.farm.animal.health.ignore_event_temporarily({
            animal: params?.animalSlug,
            farm: params?.farm,
          }),
          method: 'post',
        },
        submitButton: {
          title: Translate({ messageKey: 'ignore_temporarily' }),
        },
        subtitle: animal,
        title: Translate({ messageKey: 'ignore_temporarily' }),
      }
    },

    normalize: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        animalName: string | null
      }
    >): UseFormsProps => {
      const animal = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        level: 'warning',
        message: Translate({
          messageKey: 'do_you_really_want_to_item',
          params: {
            id: animal,
            item: Translate({
              lower: true,
              messageKey: 'assign_normal_status_to_animal',
            }),
          },
        }),
        save: {
          controller: endpoint.farm.animal.health.normalize({
            animal: params?.animalSlug,
            farm: params?.farm,
          }),
          method: 'post',
        },
        submitButton: {
          title: Translate({ messageKey: 'normalize_item' }),
        },
        subtitle: animal,
        title: Translate({
          messageKey: 'normalize_item',
          params: { item: Translate({ lower: true, messageKey: 'animal' }) },
        }),
      }
    },

    register: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        animalName: string | null
      }
    >): UseFormsProps => {
      const animal = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        controller: params?.animalSlug
          ? endpoint.farm.animal.health.event_form({
              animal: params?.animalSlug,
              farm: params?.farm,
            })
          : endpoint.farm.animal.health.event_without_animal_form(params),
        save: {
          controller: values =>
            endpoint.farm.animal.health.event_register({
              animal: params?.animalSlug ?? values.slug,
              farm: params?.farm,
            }),
          method: 'post',
        },
        subtitle: animal,
        title: Translate({
          messageKey: 'register_item',
          params: {
            item: Translate({ lower: true, messageKey: 'health_event' }),
          },
        }),
        width: 'md',
      }
    },

    revert_ignore: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        animalName: string | null
      }
    >): UseFormsProps => {
      const animal = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        level: 'warning',
        message: Translate({ messageKey: 'revert_ignore_temporarily_confirm_message' }),
        save: {
          controller: endpoint.farm.animal.health.ignore_event_temporarily({
            animal: params?.animalSlug,
            farm: params?.farm,
          }),
          method: 'delete',
        },
        submitButton: {
          title: Translate({ messageKey: 'revert_item' }),
        },
        subtitle: animal,
        title: Translate({ messageKey: 'revert_ignore_temporarily' }),
      }
    },

    update: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        event: string
        animalName: string | null
      }
    >): UseFormsProps => {
      const animal = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        controller: endpoint.farm.animal.health.event_update_form({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        save: {
          controller: endpoint.farm.animal.health.event_update({
            animal: params?.animalSlug,
            event: params?.event,
            farm: params?.farm,
          }),
          method: 'put',
        },
        subtitle: animal,
        title: Translate({
          messageKey: 'update_item',
          params: {
            item: Translate({ lower: true, messageKey: 'health_event' }),
          },
        }),
        width: 'md',
      }
    },
  },

  health_my_protocol: {
    cancel: ({
      params,
    }: FormProps<
      WithFarm & { protocolId: string; animalEarring: string; animalName?: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.health.protocols.my_protocol.cancel_form({
        farm: params?.farm,
        protocol: params?.protocolId,
      }),
      level: 'error',
      save: {
        controller: endpoint.farm.health.protocols.my_protocol.cancel({
          farm: params?.farm,
          protocol: params?.protocolId,
        }),
        method: 'delete',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'cancel' }),
      },
      subtitle: [params?.animalEarring, params?.animalName]?.filter(Boolean)?.join(' - '),
      title: Translate({
        messageKey: 'cancel_item',
        params: {
          item: Translate({ lower: true, messageKey: 'protocol' }),
        },
      }),
      width: 'sm',
    }),
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.health.protocols.my_protocol.register_form({
        farm: params?.farm,
      }),
      save: {
        controller: (values: { slug: string }) =>
          endpoint.farm.animal.health.event_register({
            animal: values?.slug,
            farm: params?.farm,
          }),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'treatment' }),
        },
      }),
      width: 'md',
    }),
    remove: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolId: string
        animalEarring: string
        animalName?: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.health.protocols.my_protocol.remove_form({
        farm: params?.farm,
        protocol: params?.protocolId,
      }),
      level: 'error',
      save: {
        controller: endpoint.farm.health.protocols.my_protocol.self({
          farm: params?.farm,
          protocol: params?.protocolId,
        }),
        method: 'delete',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'remove_item' }),
      },
      subtitle: [params?.animalEarring, params?.animalName]?.filter(Boolean)?.join(' - '),
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'protocol' }),
        },
      }),
      width: 'sm',
    }),
    update: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolId: string
        eventId: string
        animalEarring: string
        animalName?: string
        animalSlug?: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.health.protocols.my_protocol.update_form({
        farm: params?.farm,
        protocol: params?.protocolId,
      }),
      save: {
        controller: endpoint.farm.animal.health.event_remove({
          animal: params?.animalSlug,
          event: params?.eventId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      submitButton: {
        title: Translate({ messageKey: 'update_item' }),
      },
      subtitle: [params?.animalEarring, params?.animalName]?.filter(Boolean)?.join(' - '),
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'protocol' }),
        },
      }),
      width: 'md',
    }),
  },

  health_protocol_model: {
    duplicate: ({
      params,
    }: FormProps<
      WithFarm & { protocolModelId: string; protocolModelName: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.health.protocols.models.duplicate_form({
        farm: params?.farm,
        protocol: params?.protocolModelId,
      }),
      save: {
        controller: endpoint.farm.health.protocols.models.register({
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.protocolModelName,
      title: Translate({
        messageKey: 'duplicate_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model_protocol' }),
        },
      }),
      width: 'lg',
    }),
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.health.protocols.models.register_form({
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.health.protocols.models.register({
          farm: params?.farm,
        }),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model_protocol' }),
        },
      }),
      width: 'lg',
    }),
    remove: ({
      params,
    }: FormProps<
      WithFarm & { protocolModelId: string; protocolModelName: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.protocolModelName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'model_protocol' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.health.protocols.models.remove({
          farm: params?.farm,
          protocol: params?.protocolModelId,
        }),
        method: 'delete',
      },
      subtitle: params?.protocolModelName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model_protocol' }),
        },
      }),
    }),
    update: ({
      params,
    }: FormProps<
      WithFarm & { protocolModelId: string; protocolModelName?: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.health.protocols.models.update_form({
        farm: params?.farm,
        protocol: params?.protocolModelId,
      }),
      save: {
        controller: endpoint.farm.health.protocols.models.update({
          farm: params?.farm,
          protocol: params?.protocolModelId,
        }),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ lower: true, messageKey: 'model_protocol' }) },
      }),
      width: 'lg',
    }),
  },

  inseminator: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & { inseminatorId: string; inseminatorName: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.inseminatorName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'inseminator' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.inseminator.self({
          farm: params?.farm,
          inseminator: params?.inseminatorId,
        }),
        method: 'delete',
      },
      subtitle: params?.inseminatorName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'inseminator' }),
        },
      }),
    }),
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.inseminator.register_form(params),
      save: {
        controller: endpoint.farm.inseminator.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'inseminator' }),
        },
      }),
    }),
    restore: ({
      params,
    }: FormProps<
      WithFarm & { inseminatorId: string; inseminatorName: string }
    >): UseFormsProps => ({
      level: 'warning',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.inseminatorName as string,
          item: Translate({
            lower: true,
            messageKey: 'restore_item',
            params: {
              item: Translate({ lower: true, messageKey: 'inseminator' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.inseminator.restore({
          farm: params?.farm,
          inseminator: params?.inseminatorId,
        }),
        method: 'patch',
      },
      subtitle: params?.inseminatorName,
      title: Translate({
        messageKey: 'restore_item',
        params: {
          item: Translate({ lower: true, messageKey: 'inseminator' }),
        },
      }),
    }),
    update: ({
      params,
    }: FormProps<
      WithFarm & { inseminatorId: string; inseminatorName: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.inseminator.update_form({
        farm: params?.farm,
        inseminator: params?.inseminatorId,
      }),
      save: {
        controller: endpoint.farm.inseminator.self({
          farm: params?.farm,
          inseminator: params?.inseminatorId,
        }),
        method: 'put',
      },
      subtitle: params?.inseminatorName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'inseminator' }),
        },
      }),
    }),
  },

  list: {
    update: ({
      params,
    }: FormProps<WithFarm & { listID?: string }>): UseFormsProps => ({
      controller: endpoint.farm.lists.update_form(params),
      level: 'primary',
      title: Translate({ messageKey: 'update_item' }),
      width: 'lg',
    }),
  },

  mating: {
    delete: ({
      params,
    }: FormProps<WithFarm & { matingId: string; animalEarring: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'mating' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.mating.delete({
          farm: params?.farm,
          mating: params?.matingId,
        }),
        method: 'delete',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'mating' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.mating.form(params),
      save: {
        controller: endpoint.farm.mating.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'mating' }),
        },
      }),
    }),

    update: ({ params }: FormProps<WithFarm & { matingId: string }>): UseFormsProps => ({
      controller: endpoint.farm.mating.update_form({
        farm: params?.farm,
        mating: params?.matingId,
      }),
      save: {
        controller: endpoint.farm.mating.update_register({
          farm: params?.farm,
          mating: params?.matingId,
        }),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'mating' }),
        },
      }),
    }),
  },

  matrix: {
    delete: ({
      params,
    }: FormProps<WithFarm & { matrixId: string; matrixName: string }>): UseFormsProps => ({
      controller: endpoint.farm.matrix.delete_form({
        farm: params?.farm,
        matrix: params?.matrixId,
      }),
      level: 'error',
      save: {
        controller: endpoint.farm.matrix.delete({
          farm: params?.farm,
          matrix: params?.matrixId,
        }),
        method: 'delete',
      },
      submitButton: {
        props: { color: 'error' },
        title: Translate({ messageKey: 'delete_permanently' }),
      },
      subtitle: params?.matrixName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'matrix' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.matrix.register_form(params),
      save: {
        controller: endpoint.farm.matrix.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'matrix' }),
        },
      }),
      width: 'md',
    }),

    replace: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.matrix.replace_form(params),
      save: {
        controller: endpoint.farm.matrix.replace(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'correct_item',
        params: {
          item: Translate({ lower: true, messageKey: 'matrix' }),
        },
      }),
    }),

    update: ({ params }: FormProps<WithFarm & { matrixId: string }>): UseFormsProps => ({
      controller: endpoint.farm.matrix.update_form({
        farm: params?.farm,
        matrix: params?.matrixId,
      }),
      save: {
        controller: endpoint.farm.matrix.update({
          farm: params?.farm,
          matrix: params?.matrixId,
        }),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'matrix' }),
        },
      }),
      width: 'md',
    }),
  },

  milk_collection: {
    delete: ({ params }: FormProps<WithFarm & { id: string; date: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.collected_milk.remove_form(params),
      level: 'error',
      save: {
        controller: endpoint.farm.milk_control.collected_milk.remove(params),
        method: 'delete',
      },
      subtitle: params?.date,
      width: 'sm',
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.collected_milk.register_form(params),
      save: {
        controller: endpoint.farm.milk_control.collected_milk.register(params),
        method: 'post',
      },
      width: 'sm',
    }),

    update: ({ params }: FormProps<WithFarm & { id: string; date: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.collected_milk.update_form(params),
      save: {
        controller: endpoint.farm.milk_control.collected_milk.update(params),
        method: 'put',
      },
      subtitle: params?.date,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'collection' }),
        },
      }),
      width: 'sm',
    }),
  },

  milk_control: {
    delete: ({ params }: FormProps<WithFarm & { date: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ messageKey: 'production' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.milk_control.production.forms.production_delete(params),
        method: 'delete',
      },
      subtitle: Moment(params?.date).format('L'),
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'milk_control' }),
        },
      }),
    }),

    register: ({ onSuccessAction, params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.production.forms.production_form(params),
      save: {
        controller: endpoint.farm.milk_control.production.forms.production_register(params),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: onSuccessAction,
            title: Translate({
              messageKey: 'register_item',
              params: {
                item: Translate({ lower: true, messageKey: 'production' }),
              },
            }),
          },
        ],
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'milk_control' }),
        },
      }),
      width: 'xs',
    }),
  },

  milk_control_production: {
    animal_delete: ({
      params,
    }: FormProps<WithFarm & { date: string; productionId: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ messageKey: 'production' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_animal_delete({
          date: params?.date,
          farm: params?.farm,
          id: params?.productionId,
        }),
        method: 'delete',
      },
      subtitle: Moment(params?.date).format('L'),
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production' }),
        },
      }),
    }),

    animal_register: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { date: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.production.forms.per_animal_form(params),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_animal_register(params),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: () => onSuccessAction?.(),
            title: Translate({
              messageKey: 'register_item',
              params: {
                item: Translate({ lower: true, messageKey: 'other' }),
              },
            }),
          },
        ],
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production' }),
        },
      }),
      width: 'xs',
    }),

    animal_update: ({
      params,
    }: FormProps<WithFarm & { date: string; productionId: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.production.forms.per_animal_update({
        date: params?.date,
        farm: params?.farm,
        id: params?.productionId,
      }),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_animal_register(params),
        method: 'post',
      },
      subtitle: Moment(params?.date).format('L'),
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production' }),
        },
      }),
      width: 'xs',
    }),

    batch_delete: ({
      params,
    }: FormProps<WithFarm & { date: string; productionId: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ messageKey: 'production' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_batch_delete({
          date: params?.date,
          farm: params?.farm,
          id: params?.productionId,
        }),
        method: 'delete',
      },
      subtitle: Moment(params?.date).format('L'),
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production' }),
        },
      }),
    }),

    batch_register: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { date: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.production.forms.per_batch_form(params),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_batch_register(params),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: () => onSuccessAction?.(),
            title: Translate({
              messageKey: 'register_item',
              params: {
                item: Translate({ lower: true, messageKey: 'other' }),
              },
            }),
          },
        ],
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production' }),
        },
      }),
      width: 'xs',
    }),

    batch_update: ({
      params,
    }: FormProps<WithFarm & { date: string; productionId: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.production.forms.per_batch_update({
        date: params?.date,
        farm: params?.farm,
        id: params?.productionId,
      }),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_batch_register(params),
        method: 'post',
      },
      subtitle: Moment(params?.date).format('L'),
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production' }),
        },
      }),
      width: 'xs',
    }),

    total_register: ({ params }: FormProps<WithFarm & { date: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.production.forms.per_farm_form(params),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_farm_register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production_total' }),
        },
      }),
      width: 'xs',
    }),

    total_update: ({ params }: FormProps<WithFarm & { date: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.production.forms.per_farm_form(params),
      save: {
        controller: endpoint.farm.milk_control.production.forms.per_farm_register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'production_total' }),
        },
      }),
      width: 'xs',
    }),
  },

  milk_control_quality: {
    delete: ({
      params,
    }: FormProps<WithFarm & { id: string; date: string; sample: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'tank_analysis' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.milk_control.quality.remove({
          farm: params?.farm,
          id: params?.id,
        }),
        method: 'delete',
      },
      subtitle: `${params?.date} - ${Translate({ messageKey: 'sample' })}: ${params?.sample}`,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'tank_analysis' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.quality.register_form(params),
      save: {
        controller: endpoint.farm.milk_control.quality.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'tank_analysis' }),
        },
      }),
      width: 'sm',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { id: string; date: string; sample: string }>): UseFormsProps => ({
      controller: endpoint.farm.milk_control.quality.update_form({
        farm: params?.farm,
        id: params?.id,
      }),
      save: {
        controller: endpoint.farm.milk_control.quality.update({
          farm: params?.farm,
          id: params?.id,
        }),
        method: 'put',
      },
      subtitle: `${params?.date} - ${Translate({ messageKey: 'sample' })}: ${params?.sample}`,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'tank_analysis' }),
        },
      }),
      width: 'sm',
    }),
  },

  monitoring_point: {
    archive: ({
      params,
    }: FormProps<WithFarm & { point: string; pointName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'archive_item',
            params: {
              item: Translate({ lower: true, messageKey: 'monitoring_point' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.cow_comfort.point.delete(params),
        method: 'delete',
      },
      subtitle: params?.pointName,
      title: Translate({
        messageKey: 'archive_item',
        params: {
          item: Translate({ lower: true, messageKey: 'monitoring_point' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.cow_comfort.point.register_form(params),
      save: {
        controller: endpoint.farm.cow_comfort.point.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'monitoring_point' }),
        },
      }),
      width: 'md',
    }),

    revert: ({
      params,
    }: FormProps<WithFarm & { point: string; pointName: string }>): UseFormsProps => ({
      level: 'info',
      message: Translate({
        messageKey: 'do_you_really_want_to_reverse_archive_item',
        params: {
          item: Translate({ lower: true, messageKey: 'monitoring_point' }),
        },
      }),
      save: {
        controller: endpoint.farm.cow_comfort.point.update(params),
        method: 'patch',
      },
      subtitle: params?.pointName,
      title: Translate({
        messageKey: 'unarchive_item',
        params: {
          item: Translate({ lower: true, messageKey: 'monitoring_point' }),
        },
      }),
    }),

    update: ({ params }: FormProps<WithFarm & { point: string }>): UseFormsProps => ({
      controller: endpoint.farm.cow_comfort.point.update_form(params),
      save: {
        controller: endpoint.farm.cow_comfort.point.update(params),
        method: 'put',
      },
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'monitoring_point' }),
        },
      }),
      width: 'md',
    }),
  },

  new_bull_semen: {
    archive: ({ params }: FormProps<WithFarm & { semen: string; name: string }>): UseFormsProps => {
      return {
        alert: {
          level: 'warning',
          text: (
            <MarkdownView
              style={{ backgroundColor: 'transparent', color: 'currentColor', textAlign: 'left' }}
            >
              {Translate({ messageKey: 'bull_or_semen_archive_message' })}
            </MarkdownView>
          ),
        },
        level: 'warning',
        save: {
          controller: endpoint.farm.new_semen.archive(params),
          method: 'patch',
        },
        submitButton: {
          title: Translate({ messageKey: 'archive_item' }),
        },
        subtitle: params?.name,
        title: Translate({
          messageKey: 'archive_item',
          params: { item: Translate({ lower: true, messageKey: 'bull_or_semen' }) },
        }),
      }
    },
    delete: ({ params }: FormProps<WithFarm & { semen: string }>): UseFormsProps => ({
      controller: endpoint.farm.new_semen.remove_form(params),
      level: 'error',
      width: 'md',
    }),
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.new_semen.register_form(params),
      width: 'md',
    }),
    unarchive: ({ params }: FormProps<WithFarm & { semen: string }>): UseFormsProps => ({
      controller: endpoint.farm.new_semen.unarchive_form(params),
      width: 'md',
    }),
    update: ({ params }: FormProps<WithFarm & { semen: string; name: string }>): UseFormsProps => ({
      controller: endpoint.farm.new_semen.update_form(params),
      subtitle: params?.name,
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ lower: true, messageKey: 'bull_or_semen' }) },
      }),
      width: 'md',
    }),
  },

  nutrition_diet: {
    delete: ({
      params,
    }: FormProps<WithFarm & { dietSlug: string; dietName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.dietName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'diet_label' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.nutrition.diet.remove({
          diet: params?.dietSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.dietName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'diet_label' }),
        },
      }),
      width: 'md',
    }),

    duplicate: ({ params }: FormProps<WithFarm & { dietSlug: string }>): UseFormsProps => ({
      controller: endpoint.farm.nutrition.diet.update_form({
        diet: params?.dietSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.nutrition.diet.register({
          diet: params?.dietSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      title: Translate({
        messageKey: 'duplicate_item',
        params: {
          item: Translate({ lower: true, messageKey: 'diet_label' }),
        },
      }),
      width: 'lg',
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.nutrition.diet.register_form(params),
      save: {
        controller: endpoint.farm.nutrition.diet.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'diet_label' }),
        },
      }),
      width: 'lg',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { dietSlug: string; dietName: string }>): UseFormsProps => ({
      controller: endpoint.farm.nutrition.diet.update_form({
        diet: params?.dietSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.nutrition.diet.update({
          diet: params?.dietSlug,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.dietName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'diet_label' }),
        },
      }),
      width: 'lg',
    }),
  },

  nutrition_ingredient: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & { ingredientSlug: string; ingredientName: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.ingredientName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'ingredient' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.nutrition.ingredient.remove({
          farm: params?.farm,
          ingredient: params?.ingredientSlug,
        }),
        method: 'delete',
      },
      subtitle: params?.ingredientName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'ingredient' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.nutrition.ingredient.register_form(params),
      save: {
        controller: endpoint.farm.nutrition.ingredient.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'ingredient' }),
        },
      }),
    }),

    update: ({
      params,
    }: FormProps<
      WithFarm & { ingredientSlug: string; ingredientName: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.nutrition.ingredient.update_form({
        farm: params?.farm,
        ingredient: params?.ingredientSlug,
      }),
      save: {
        controller: endpoint.farm.nutrition.ingredient.update({
          farm: params?.farm,
          ingredient: params?.ingredientSlug,
        }),
        method: 'put',
      },
      subtitle: params?.ingredientName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'ingredient' }),
        },
      }),
    }),
  },

  protocol: {
    delete: ({
      params,
    }: FormProps<WithFarm & { protocolSlug: string; protocolName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.protocolName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'protocol' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.protocol.remove({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        method: 'delete',
      },
      subtitle: params?.protocolName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'protocol' }),
        },
      }),
    }),

    register: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals?: {
          slug: string
          earring: string
          name: string | null
        }[]
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.protocol.register_form(params),
      params: { animals: params?.selectedAnimals?.map(animal => animal.slug) },
      save: {
        controller: endpoint.farm.protocol.register(params),
        method: 'post',
      },
      subtitle: Translate({ messageKey: 'protocol_subtitle' }),
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'protocol' }),
        },
      }),
      width: 'lg',
    }),

    update: ({ params }: FormProps<WithFarm & { protocolSlug: string }>): UseFormsProps => ({
      controller: endpoint.farm.protocol.update_form({
        farm: params?.farm,
        protocol: params?.protocolSlug,
      }),
      save: {
        controller: endpoint.farm.protocol.update({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        method: 'put',
      },
      subtitle: Translate({ messageKey: 'protocol_subtitle' }),
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'protocol' }),
        },
      }),
      width: 'lg',
    }),
  },

  protocol_animal: {
    add_animals: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: {
          slug: string
          earring: string
          name: string | null
        }[]
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.protocol.add_animals({
        farm: params?.farm,
      }),
      params: { animals: params?.selectedAnimals?.map(animal => animal.slug) },
      save: {
        controller: values =>
          endpoint.farm.protocol.add_animal({
            farm: params?.farm,
            protocol: values?.protocol,
          }),
        method: 'put',
      },
      title: Translate({ messageKey: 'protocol_add_animal' }),
    }),
    delete: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolSlug: string
        animalsSlug: string[]
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.protocol.remove_multiple_animal_form({
        farm: params?.farm,
        protocol: params?.protocolSlug,
      }),
      level: 'error',
      params: { animals: params?.animalsSlug },
      save: {
        controller: endpoint.farm.protocol.remove_multiple_animal({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        manipulateParams: value => ({
          ...value,
          animals: params?.animalsSlug?.map(value => ({ slug: value })),
        }),
        method: 'delete',
      },
      title: Translate({ messageKey: 'protocol_remove_animal' }),
    }),
    recovery: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolSlug: string
        animalSlug: string
        animalName: string | null
        animalEarring: string
      }
    >): UseFormsProps => {
      const animal = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        level: 'warning',
        message: Translate({
          messageKey: 'do_you_really_want_to_item',
          params: {
            id: animal as string,
            item: Translate({
              lower: true,
              messageKey: 'restore_item',
              params: { item: Translate({ messageKey: 'animal' }) },
            }),
          },
        }),
        save: {
          controller: endpoint.farm.protocol.restore_animal({
            animal: params?.animalSlug,
            farm: params?.farm,
            protocol: params?.protocolSlug,
          }),
          method: 'patch',
        },
        subtitle: animal,
        title: Translate({
          messageKey: 'restore_item',
          params: {
            item: Translate({ lower: true, messageKey: 'animal' }),
          },
        }),
      }
    },

    register: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolSlug: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.protocol.add_animal_form({
        farm: params?.farm,
        protocol: params?.protocolSlug,
      }),
      save: {
        controller: endpoint.farm.protocol.add_animal({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        method: 'put',
      },
      title: Translate({ messageKey: 'protocol_add_animal' }),
    }),
  },

  protocol_model: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & { protocolModelId: string; protocolModelName: string }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.protocolModelName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'model_protocol' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.protocol.protocol_model_delete({
          farm: params?.farm,
          protocol: params?.protocolModelId,
        }),
        method: 'delete',
      },
      subtitle: params?.protocolModelName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model_protocol' }),
        },
      }),
    }),

    duplicate: ({ params }: FormProps<WithFarm & { protocolModelId: string }>): UseFormsProps => ({
      controller: endpoint.farm.protocol.protocol_model_duplicate({
        farm: params?.farm,
        protocol: params?.protocolModelId,
      }),
      save: {
        controller: endpoint.farm.protocol.protocol_model_register({
          farm: params?.farm,
          protocol: params?.protocolModelId,
        }),
        method: 'post',
      },
      subtitle: Translate({ messageKey: 'protocol_model_subtitle' }),
      title: Translate({
        messageKey: 'duplicate_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model_protocol' }),
        },
      }),
      width: 'lg',
    }),

    recovery: ({
      params,
    }: FormProps<
      WithFarm & { protocolModelId: string; protocolModelName: string }
    >): UseFormsProps => ({
      level: 'info',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.protocolModelName as string,
          item: Translate({
            lower: true,
            messageKey: 'restore_item',
            params: {
              item: Translate({ lower: true, messageKey: 'model_protocol' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.protocol.protocol_model_restore({
          farm: params?.farm,
          protocol: params?.protocolModelId,
        }),
        method: 'patch',
      },
      subtitle: params?.protocolModelName,
      title: Translate({
        messageKey: 'restore_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model_protocol' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.protocol.protocol_model_register_form(params),
      save: {
        controller: endpoint.farm.protocol.protocol_model_register(params),
        method: 'post',
      },
      subtitle: Translate({ messageKey: 'protocol_model_subtitle' }),
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model' }),
        },
      }),
      width: 'lg',
    }),

    update: ({ params }: FormProps<WithFarm & { protocolModelId: string }>): UseFormsProps => ({
      controller: endpoint.farm.protocol.protocol_model_update_form({
        farm: params?.farm,
        protocol: params?.protocolModelId,
      }),
      save: {
        controller: endpoint.farm.protocol.protocol_model_update({
          farm: params?.farm,
          protocol: params?.protocolModelId,
        }),
        method: 'put',
      },
      subtitle: Translate({ messageKey: 'protocol_model_subtitle' }),
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'model_protocol' }),
        },
      }),
      width: 'lg',
    }),
  },

  protocol_reproduction_method: {
    et: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolSlug: string
        selectedAnimals: {
          slug: string
          earring: string
          name: string | null
        }[]
        build: any
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        buttons: {
          extra: [
            {
              onClick: () =>
                params?.build?.({
                  formName: 'protocol_reproduction_method',
                  formType: 'et_with_details',
                  params,
                }),
              title: Translate({ messageKey: 'register_more_details' }),
            },
          ],
        },
        controller: endpoint.farm.protocol.register_reproduction_et_form({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.protocol.register_et({
            farm: params?.farm,
            protocol: params?.protocolSlug,
          }),
          manipulateParams: values => ({
            ...values,
            animals: params?.selectedAnimals.map(animal => ({
              ...values,
              slug: animal.slug,
            })),
          }),
          method: 'post',
        },
        subtitle: subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: { item: Translate({ messageKey: 'ET' }) },
        }),
      }
    },

    et_with_details: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolSlug: string
        selectedAnimals: {
          slug: string
          earring: string
          name: string | null
        }[]
        build: any
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        buttons: {
          extra: [
            {
              onClick: () =>
                params?.build?.({
                  formName: 'protocol_reproduction_method',
                  formType: 'et',
                  params,
                }),
              title: Translate({
                messageKey: 'register_item',
                params: { item: Translate({ lower: true, messageKey: 'less_details' }) },
              }),
            },
          ],
        },
        controller: endpoint.farm.protocol.register_et_form({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.protocol.register_et({
            farm: params?.farm,
            protocol: params?.protocolSlug,
          }),
          method: 'post',
        },
        subtitle: subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: { item: Translate({ messageKey: 'ET' }) },
        }),
        width: 'xl',
      }
    },

    ia: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolSlug: string
        selectedAnimals: {
          slug: string
          earring: string
          name: string | null
        }[]
        build: any
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        buttons: {
          extra: [
            {
              onClick: () =>
                params?.build?.({
                  formName: 'protocol_reproduction_method',
                  formType: 'ia_with_details',
                  params,
                }),
              title: Translate({ messageKey: 'register_more_details' }),
            },
          ],
        },
        controller: endpoint.farm.protocol.register_reproduction_form({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.protocol.register_ai({
            farm: params?.farm,
            protocol: params?.protocolSlug,
          }),
          manipulateParams: values => ({
            ...values,
            animals: params?.selectedAnimals.map(animal => ({
              ...values,
              slug: animal.slug,
            })),
          }),
          method: 'post',
        },
        subtitle: subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: { item: Translate({ messageKey: 'AI' }) },
        }),
      }
    },

    ia_with_details: ({
      params,
    }: FormProps<
      WithFarm & {
        protocolSlug: string
        selectedAnimals: {
          slug: string
          earring: string
          name: string | null
        }[]
        build: any
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        buttons: {
          extra: [
            {
              onClick: () =>
                params?.build?.({
                  formName: 'protocol_reproduction_method',
                  formType: 'ia',
                  params,
                }),
              title: Translate({
                messageKey: 'register_item',
                params: { item: Translate({ lower: true, messageKey: 'less_details' }) },
              }),
            },
          ],
        },
        controller: endpoint.farm.protocol.register_ai_form({
          farm: params?.farm,
          protocol: params?.protocolSlug,
        }),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.protocol.register_ai({
            farm: params?.farm,
            protocol: params?.protocolSlug,
          }),
          method: 'post',
        },
        subtitle: subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: { item: Translate({ messageKey: 'AI' }) },
        }),
        width: 'lg',
      }
    },
  },

  reproduction_events: {
    abortion_quickaction: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.abortion.form_without_animal(params),
      save: {
        controller: values =>
          endpoint.farm.animal.event.reproduction_event({
            animal: values?.slug,
            farm: params?.farm,
          }),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'abortion' }),
        },
      }),
      width: 'xs',
    }),

    abortion_register: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.abortion.form(params),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'abortion' }),
        },
      }),
      width: 'xs',
    }),

    abortion_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.abortion.update_form(params),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'abortion' }),
        },
      }),
      width: 'xs',
    }),

    apt_after_delivery_update: ({
      params,
    }: FormProps<
      WithFarm & {
        animalSlug: string
        animalEarring: string
        event: string
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.apt_after_delivery.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'event' }),
        },
      }),
      width: 'xs',
    }),

    calving_many: ({ build, params }: FormProps<WithFarm>): UseFormsProps => {
      return {
        controller: endpoint.farm.event.reproduction.many_delivery_form(params),
        save: {
          controller: endpoint.farm.event.reproduction.many_events(params),
          method: 'post',
          onSuccessActions: [
            {
              enabled: ({ values }) => Boolean(values?.animals?.some?.(isLiveCalf)),
              onClick: ({ response }) => {
                // apenas partos com calves vivos geram registro de animal
                const calvings = response?.data
                  ?.map(calving => ({
                    ...calving,
                    parameters: { ...calving?.parameters, calves: getLiveCalves(calving) },
                  }))
                  .filter(calving => countLiveCalves(calving) > 0)
                const firstCalving = calvings?.[0]

                if (!firstCalving) return

                onSuccessActionBirthRegister({
                  build,
                  calves: countLiveCalves(firstCalving),
                  calvings,
                  eventSlug: firstCalving?.slug,
                  farmSlug: params?.farm,
                })
              },
              title: Translate({ messageKey: 'register_new_animal' }),
            },
          ],
        },
        title: Translate({
          messageKey: 'register_item',
          params: {
            item: Translate({ lower: true, messageKey: 'calving' }),
          },
        }),
        width: 'lg',
      }
    },

    calving_register: ({
      build,
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.calving.form({ farm: params?.farm }),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
        onSuccessActions: [
          {
            enabled: ({ values }) => Boolean(values?.calves?.some(isLiveCalf)),
            onClick: ({ response }) => {
              onSuccessActionBirthRegister({
                build,
                calves: countLiveCalves(response?.data),
                eventSlug: response?.data?.slug,
                farmSlug: params?.farm,
              })
            },
            title: Translate({ messageKey: 'register_new_animal' }),
          },
        ],
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'calving' }),
        },
      }),
      width: 'sm',
    }),

    calving_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.calving.update_form(params),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'calving' }),
        },
      }),
      width: 'sm',
    }),

    delete: ({
      params,
    }: FormProps<
      WithFarm & {
        eventSlug: string
        eventName: string
        animalSlug: string
      }
    >): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.eventName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({
                lower: true,
                messageKey: 'reproduction_event_this',
              }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.eventSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.eventName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'reproduction_event' }),
        },
      }),
    }),

    drying_quickaction: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.dry.form_without_animal(params),
      save: {
        controller: values =>
          endpoint.farm.animal.event.reproduction_event({
            animal: values?.slug,
            farm: params?.farm,
          }),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'drying' }),
        },
      }),
      width: 'md',
    }),

    drying_register: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: {
          slug: string
          name: string | null
          earring: string
        }[]
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        controller: endpoint.farm.dry.form({ farm: params?.farm }),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.animal.event.reproduction_event_many({
            farm: params?.farm,
          }),
          manipulateParams: values => ({
            ...values,
            animals: params?.selectedAnimals?.map(animal => animal.slug),
          }),
          method: 'post',
        },
        subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: {
            item: Translate({ lower: true, messageKey: 'drying' }),
          },
        }),
        width: 'md',
      }
    },

    drying_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.dry.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'drying' }),
        },
      }),
      width: 'md',
    }),

    embryo_transfer_many: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.event.reproduction.many_et_form(params),
      save: {
        controller: endpoint.farm.event.reproduction.many_events(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'embryo_transfer' }),
        },
      }),
      width: 'lg',
    }),

    embryo_transfer_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.embryo_transfer.form({ farm: params?.farm }),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'embryo_transfer' }),
        },
      }),
      width: 'xs',
    }),

    embryo_transfer_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.embryo_transfer.update_form({
        event: params?.event,
        farm: params?.farm,
      }),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'embryo_transfer' }),
        },
      }),
      width: 'xs',
    }),

    empty_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.event.reproduction.empty_update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'empty_animal' }),
        },
      }),
      width: 'xs',
    }),

    gynecologic_exam_apt_register_many: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: { slug: string; name?: string; earring: string }[]
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        controller: endpoint.farm.gynecologic.apt.form(params),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.animal.event.reproduction_event_many(params),
          method: 'post',
        },
        subtitle,
        title: Translate({ messageKey: 'release_to_reproduction' }),
        width: 'xs',
      }
    },

    gynecologic_exam_apt_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.gynecologic.apt.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'gynecologic_examination',
          }),
        },
      }),
      width: 'xs',
    }),

    gynecologic_exam_inapt_register_many: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: { slug: string; name?: string; earring: string }[]
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')
      return {
        controller: endpoint.farm.gynecologic.inapt.form(params),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.animal.event.reproduction_event_many(params),
          method: 'post',
        },
        subtitle,
        title: Translate({ messageKey: 'reproduction_restriction' }),
        width: 'xs',
      }
    },

    gynecologic_exam_inapt_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.gynecologic.inapt.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'gynecologic_examination',
          }),
        },
      }),
      width: 'xs',
    }),

    heat_confirm: ({
      params,
    }: FormProps<
      WithFarm & {
        eventSlug: string
        animalEarring: string
        animalName: string | null
        animalSlug?: string
      }
    >): UseFormsProps => {
      const subtitle = [params?.animalEarring, params?.animalName].filter(Boolean).join(' - ')

      return {
        controller: endpoint.farm.heat.form({
          event: params?.eventSlug,
          farm: params?.farm,
        }),
        params: { animal: params?.animalSlug },
        save: {
          controller: endpoint.farm.heat.confirm({
            event: params?.eventSlug,
            farm: params?.farm,
          }),
          method: 'post',
        },
        subtitle,
        title: Translate({
          messageKey: 'confirmation_of_item',
          params: {
            item: Translate({ lower: true, messageKey: 'heat_alert' }),
          },
        }),
        width: 'xs',
      }
    },

    heifer_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.event.reproduction.heifer_register_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({ messageKey: 'release_to_reproduction' }),
      width: 'xs',
    }),

    heifer_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.event.reproduction.heifer_update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'change_to_heifer' }),
        },
      }),
      width: 'xs',
    }),

    induced_lactation_many: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: { slug: string; name?: string; earring: string }[]
      }
    >): UseFormsProps => ({
      controller: endpoint.farm.event.reproduction.many_induced_lactation_form(params),
      params: {
        animals: params?.selectedAnimals?.map(animal => animal.slug),
      },
      save: {
        controller: endpoint.farm.event.reproduction.many_induced_lactation(params),
        method: 'post',
      },
      subtitle: params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', '),
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'induced_lactation' }),
        },
      }),
      width: 'lg',
    }),

    induced_lactation_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.induced_lactation.form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'induced_lactation' }),
        },
      }),
      width: 'xs',
    }),

    induced_lactation_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.induced_lactation.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'induced_lactation' }),
        },
      }),
      width: 'xs',
    }),

    insemination_many: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.event.reproduction.many_ai_form(params),
      save: {
        controller: endpoint.farm.event.reproduction.many_events(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'insemination' }),
        },
      }),
      width: 'lg',
    }),

    insemination_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.insemination.form({ farm: params?.farm }),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'insemination' }),
        },
      }),
      width: 'xs',
    }),

    insemination_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.insemination.update_form(params),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'insemination' }),
        },
      }),
      width: 'xs',
    }),

    natural_breeding_many: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.event.reproduction.many_nm_form(params),
      save: {
        controller: endpoint.farm.event.reproduction.many_events(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'natural_mating' }),
        },
      }),
      width: 'lg',
    }),

    natural_breeding_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.natural_breeding.form({ farm: params?.farm }),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'natural_mating' }),
        },
      }),
      width: 'xs',
    }),

    natural_breeding_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.natural_breeding.update_form(params),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'natural_mating' }),
        },
      }),
      width: 'xs',
    }),

    pre_calving_register: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: {
          slug: string
          name: string | null
          earring: string
        }[]
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        controller: endpoint.farm.pre_calving.form({ farm: params?.farm }),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.animal.event.reproduction_event_many({
            farm: params?.farm,
          }),
          manipulateParams: values => ({
            ...values,
            animals: params?.selectedAnimals?.map(animal => animal.slug),
          }),
          method: 'post',
        },
        subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: {
            item: Translate({ lower: true, messageKey: 'pre_calving' }),
          },
        }),
        width: 'xs',
      }
    },

    pre_calving_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.pre_calving.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'pre_calving' }),
        },
      }),
      width: 'xs',
    }),

    pregnancy_diagnosis_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.pregnancy.form({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'pregnancy_diagnosis' }),
        },
      }),
    }),

    pregnancy_diagnosis_register_many: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: {
          slug: string
          name: string | null
          earring: string
        }[]
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')

      return {
        controller: endpoint.farm.pregnancy.many.first(params),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.animal.event.reproduction_event_many({
            farm: params?.farm,
          }),
          method: 'post',
        },
        subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: {
            item: Translate({ lower: true, messageKey: 'pregnancy_diagnosis' }),
          },
        }),
      }
    },

    pregnancy_diagnosis_register_quickaction: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.pregnancy.form_without_animal({
        animal: params?.animalSlug,
        farm: params?.farm,
      }),
      save: {
        controller: values =>
          endpoint.farm.animal.event.reproduction_event({
            animal: values?.slug,
            farm: params?.farm,
          }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'pregnancy_diagnosis' }),
        },
      }),
    }),

    pregnancy_diagnosis_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.pregnancy.update_form(params),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'pregnancy_diagnosis' }),
        },
      }),
    }),

    recovery: ({
      params,
    }: FormProps<
      WithFarm & {
        eventSlug: string
        eventName: string
      }
    >): UseFormsProps => ({
      level: 'warning',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: Translate({ lower: true, messageKey: 'event' }),
          item: Translate({ lower: true, messageKey: 'restore_this' }),
        },
      }),
      save: {
        controller: endpoint.farm.event.restore({
          event: params?.eventSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      submitButton: {
        props: { color: 'warning' },
        title: Translate({ messageKey: 'restore' }),
      },
      subtitle: params?.eventName,
      title: Translate({ messageKey: 'restore_event' }),
    }),

    reproductive_evaluation_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.reproductive_evaluation.register_form({ farm: params?.farm }),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.reproductive_evaluation.register({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'reproductive_evaluation' }),
        },
      }),
      width: 'xs',
    }),

    reproductive_evaluation_register_many: ({
      params,
    }: FormProps<
      WithFarm & { selectedAnimals: Array<{ slug: string; earring: string; name: string | null }> }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join('-'))
        .join(', ')

      return {
        controller: endpoint.farm.reproductive_evaluation.register_many_form({
          farm: params?.farm,
        }),
        params: { animals: params?.selectedAnimals?.map(animal => animal.slug) },
        save: {
          controller: endpoint.farm.reproductive_evaluation.register_many({
            farm: params?.farm,
          }),
        },
        subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: {
            item: Translate({ lower: true, messageKey: 'reproductive_evaluation' }),
          },
        }),
        width: 'xs',
      }
    },

    reproductive_evaluation_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.reproductive_evaluation.update_form({
        event: params?.event,
        farm: params?.farm,
      }),
      params: { animal: params?.animalSlug },
      save: {
        controller: endpoint.farm.reproductive_evaluation.update({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'reproductive_evaluation' }),
        },
      }),
      width: 'xs',
    }),

    unconfirm: ({
      params,
    }: FormProps<
      WithFarm & {
        eventSlug: string
        eventName: string
        eventType: string
      }
    >): UseFormsProps => ({
      level: 'warning',
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'revert_item',
            params: {
              item: Translate({
                messageKey: 'confirmation_of_item',
                params: {
                  item: params?.eventType?.match(/birth+/)
                    ? Translate({ lower: true, messageKey: 'calving' })
                    : Translate({ lower: true, messageKey: 'heat' }),
                },
              }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.farm.heat.unconfirm({
          event: params?.eventSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      submitButton: {
        props: { color: 'warning' },
        title: Translate({ messageKey: 'revert_item' }),
      },
      subtitle: params?.eventName,
      title: Translate({
        messageKey: 'revert_item',
        params: {
          item: Translate({ lower: true, messageKey: 'confirmation' }),
        },
      }),
    }),

    visual_heat_quickaction: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.visual_heat.form_without_animal(params),
      save: {
        controller: values =>
          endpoint.farm.animal.event.reproduction_event({
            animal: values?.slug,
            farm: params?.farm,
          }),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: onSuccessAction,
            title: Translate({
              messageKey: 'register_item',
              params: {
                item: Translate({ lower: true, messageKey: 'insemination' }),
              },
            }),
          },
        ],
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'visual_heat' }),
        },
      }),
      width: 'xs',
    }),

    visual_heat_register: ({
      onSuccessAction,
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.visual_heat.form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
        onSuccessActions: [
          {
            enabled: () => true,
            onClick: onSuccessAction,
            title: Translate({
              messageKey: 'register_item',
              params: {
                item: Translate({ lower: true, messageKey: 'insemination' }),
              },
            }),
          },
        ],
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'visual_heat' }),
        },
      }),
      width: 'xs',
    }),

    visual_heat_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.visual_heat.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'visual_heat' }),
        },
      }),
      width: 'xs',
    }),

    weaning_quickaction: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.weaning.form_without_animal(params),
      save: {
        controller: values =>
          endpoint.farm.animal.event.reproduction_event({
            animal: values?.slug,
            farm: params?.farm,
          }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'weaning' }),
        },
      }),
      width: 'xs',
    }),

    weaning_register: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalEarring: string }>): UseFormsProps => ({
      controller: endpoint.farm.weaning.form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'weaning' }),
        },
      }),
      width: 'xs',
    }),

    weaning_register_many: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedAnimals: { slug: string; name?: string; earring: string }[]
      }
    >): UseFormsProps => {
      const subtitle = params?.selectedAnimals
        ?.map(({ earring, name }) => [earring, name].filter(Boolean).join(' - '))
        .join(', ')
      return {
        controller: endpoint.farm.weaning.many_form(params),
        params: {
          animals: params?.selectedAnimals?.map(animal => animal.slug),
        },
        save: {
          controller: endpoint.farm.animal.event.reproduction_event_many(params),
          manipulateParams: value => ({
            ...value,
            animals: params?.selectedAnimals?.map(animal => animal.slug),
          }),
          method: 'post',
        },
        subtitle,
        title: Translate({
          messageKey: 'register_item',
          params: {
            item: Translate({ lower: true, messageKey: 'weaning' }),
          },
        }),
        width: 'xs',
      }
    },

    weaning_update: ({
      params,
    }: FormProps<
      WithFarm & { animalSlug: string; animalEarring: string; event: string }
    >): UseFormsProps => ({
      controller: endpoint.farm.weaning.update_form(params),
      save: {
        controller: endpoint.farm.animal.event.reproduction_event_self({
          animal: params?.animalSlug,
          event: params?.event,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.animalEarring,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'weaning' }),
        },
      }),
      width: 'xs',
    }),
  },

  tasks: {
    reject: ({
      params,
    }: FormProps<
      WithFarm & {
        animal: string
        treatment: string
        stage: string
        farm_task_id: string
      }
    >): UseFormsProps => ({
      level: 'warning',
      message: Translate({ messageKey: 'mark_as_not_applied_warning' }),
      save: {
        controller: endpoint.farm.tasks.reject({ farm: params?.farm, task: params?.farm_task_id }),
        method: 'put',
      },
      subtitle: `${Translate({ messageKey: 'animal' })}: ${params?.animal}, ${Translate({ messageKey: 'application' })}: ${params?.stage}, ${Translate({ messageKey: 'medication' })}: ${params?.treatment}`,
      title: Translate({
        messageKey: 'mark_as_item',
        params: { item: Translate({ lower: true, messageKey: 'not_applied' }) },
      }),
    }),
    reject_bulk: ({
      params,
    }: FormProps<
      WithFarm & {
        selectedTasks: {
          animal: string
          treatment: string
          stage: string
          farm_task_id: string
        }[]
      }
    >): UseFormsProps => ({
      level: 'warning',
      message: Translate({ messageKey: 'mark_as_not_applied_bulk_warning' }),
      save: {
        controller: endpoint.farm.tasks.bulk.reject({ farm: params?.farm }),
        manipulateParams(values) {
          return {
            ...values,
            farm_tasks: params?.selectedTasks.map(task => task.farm_task_id),
          }
        },
        method: 'put',
      },
      subtitle: params?.selectedTasks
        ?.map(
          task =>
            `${Translate({ messageKey: 'animal' })}: ${task?.animal}, ${Translate({ messageKey: 'application' })}: ${task?.stage}, ${Translate({ messageKey: 'medication' })}: ${task?.treatment}`,
        )
        .join('\n'),
      title: Translate({
        messageKey: 'mark_as_item',
        params: { item: Translate({ lower: true, messageKey: 'not_applied_plural' }) },
      }),
    }),
    reschedule: ({
      params,
    }: FormProps<WithFarm & { stage: string; farm_task_id: string }>): UseFormsProps => ({
      level: 'warning',
      message: Translate({ messageKey: 'do_you_really_want_to_revert_this_application' }),
      save: {
        controller: endpoint.farm.tasks.reschedule({
          farm: params?.farm,
          task: params?.farm_task_id,
        }),
        method: 'put',
      },
      subtitle: `${Translate({ messageKey: 'application' })}: ${params?.stage}`,
      title: Translate({
        messageKey: 'revert_item',
        params: { item: Translate({ lower: true, messageKey: 'application' }) },
      }),
    }),
  },

  tokenized_animals: {
    delete: ({
      params,
    }: FormProps<WithFarm & { animalSlug: string; animalName?: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'remove_tokenized_animal',
          }),
        },
      }),
      save: {
        controller: endpoint.farm.tokens.simple_tokens.remove({
          animal: params?.animalSlug,
          farm: params?.farm,
        }),
        method: 'delete',
      },
      subtitle: params?.animalName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'animal' }),
        },
      }),
    }),

    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.tokens.simple_tokens.register_form(params),
      save: {
        controller: endpoint.farm.tokens.simple_tokens.register(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'tokenizable_animals' }),
        },
      }),
    }),
  },
}
// @ts-ignore
export { farmForms }
